import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import {
  Box,
  Skeleton,
  SxProps,
  TableContainer,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
// @ts-ignore
import NoDataImage from "assets/images/noData.svg";
import { PaginationV2 } from "components/pagination";
import * as React from "react";
import { TableCell } from "./TableCell";
import { TableHeader } from "./TableHeader";
import { useColumnOrderPersistence } from "./useColumnOrderPersistence";
import { useColumnSizingPersistence } from "./useColumnSizingPersistence";

interface TableProps<TData> {
  data: TData[];
  // biome-ignore lint/suspicious/noExplicitAny: <>
  columns: ColumnDef<TData, any>[];
  isLoading?: boolean;
  rowCount?: number;
  pageSize?: number;
  pageIndex?: number;
  onPaginationChange?: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  onSortingChange?: OnChangeFn<SortingState>;
  sorting?: SortingState;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  enableRowSelection?: ((row: Row<TData>) => boolean) | boolean;
  uniqueKey?: string;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  customActionToolbar?: React.ReactNode;
  showActionToolbar?: boolean;
  paginationLeftActionComponent?: React.ReactNode;
  enableColumnResizing?: boolean;
  enableColumnReordering?: boolean;
  tableKey?: string;
  initialColumnOrder?: string[];
  onColumnOrderChange?: (columnOrder: string[]) => void;
}

export function Table<TData>({
  data,
  columns,
  isLoading = false,
  rowCount = 0,
  pageSize = 10,
  pageIndex = 0,
  onPaginationChange,

  onSortingChange,
  sorting,
  sx,
  containerSx,
  onRowClick,
  enableRowSelection = true,
  uniqueKey = "mapping_id",
  onRowSelectionChange,
  rowSelection,
  customActionToolbar,
  showActionToolbar = false,
  paginationLeftActionComponent,
  enableColumnResizing = false,
  enableColumnReordering = false,
  tableKey,
  initialColumnOrder,
  onColumnOrderChange,
}: TableProps<TData>) {
  const theme = useTheme();

  const resolvedColumns = React.useMemo(() => {
    if (!enableColumnResizing) return columns;

    return columns.map((col) => {
      const meta = col.meta as
        | {
            enableResizing?: boolean;
            size?: number;
            minSize?: number;
            maxSize?: number;
          }
        | undefined;
      const isCheckbox = col.header === "select";

      return {
        ...col,
        enableResizing: isCheckbox ? false : meta?.enableResizing !== false,
        size: col.size ?? meta?.size ?? 160,
        minSize: col.minSize ?? meta?.minSize ?? 40,
        maxSize: col.maxSize ?? meta?.maxSize ?? 800,
      };
    });
  }, [columns, enableColumnResizing]);

  const [columnSizing, setColumnSizing] = useColumnSizingPersistence(
    tableKey,
    enableColumnResizing,
  );

  const [columnOrder, setColumnOrder] = useColumnOrderPersistence(
    tableKey,
    enableColumnReordering,
    initialColumnOrder,
  );

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableMultiRowSelection: true,
    enableSortingRemoval: false,
    rowCount,
    enableRowSelection: enableRowSelection,
    getRowId: (row: TData) => String(row[uniqueKey as keyof TData]),
    onRowSelectionChange,
    onSortingChange,
    enableColumnResizing,
    columnResizeMode: "onChange",
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    state: {
      pagination: {
        pageIndex: pageIndex || 0,
        pageSize: pageSize || 10,
      },
      rowSelection: rowSelection || {},
      sorting: sorting || [],
      columnSizing,
      columnOrder,
    },
  });

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const currentOrder = table.getAllLeafColumns().map((c) => c.id);
      const oldIndex = currentOrder.indexOf(active.id as string);
      const newIndex = currentOrder.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
      table.setColumnOrder(newOrder);
      onColumnOrderChange?.(newOrder);
    },
    [table, onColumnOrderChange],
  );

  const columnIds = React.useMemo(
    () => table.getAllLeafColumns().map((c) => c.id),
    [table],
  );

  const getSkeletonCellSx = (
    columnId: string,
    canResize: boolean,
    isFirst: boolean,
  ) => {
    const hasSized = canResize && columnId in columnSizing;
    if (hasSized) {
      const size = table.getColumn(columnId)?.getSize();
      return {
        width: `${size}px`,
        minWidth: `${size}px`,
        maxWidth: `${size}px`,
        overflow: "hidden" as const,
      };
    }
    return {
      minWidth: isFirst ? "30px" : "140px",
      maxWidth: "200px",
    };
  };

  const handleRowClick = (row: TData) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const anchorRowIdRef = React.useRef<string | null>(null);

  const handleCheckboxToggle = React.useCallback(
    (row: Row<TData>, isShiftKey: boolean) => {
      const rows = table.getRowModel().rows;
      const currentIndex = rows.findIndex((r) => r.id === row.id);
      const targetChecked = !row.getIsSelected();
      const anchorIndex = anchorRowIdRef.current
        ? rows.findIndex((r) => r.id === anchorRowIdRef.current)
        : -1;

      if (isShiftKey && anchorIndex !== -1 && currentIndex !== -1) {
        const [start, end] =
          anchorIndex < currentIndex
            ? [anchorIndex, currentIndex]
            : [currentIndex, anchorIndex];
        table.setRowSelection((prev) => {
          const next = { ...prev };
          for (let i = start; i <= end; i++) {
            const r = rows[i];
            if (!r.getCanSelect()) continue;
            if (targetChecked) {
              next[r.id] = true;
            } else {
              delete next[r.id];
            }
          }
          return next;
        });
      } else {
        row.toggleSelected(targetChecked);
      }
      anchorRowIdRef.current = row.id;
    },
    [table],
  );

  const renderRowCells = (
    row: ReturnType<typeof table.getRowModel>["rows"][number],
  ) =>
    row
      .getVisibleCells()
      .map((cell) => (
        <TableCell
          key={cell.id}
          cell={cell}
          enableColumnResizing={enableColumnResizing}
          columnSizing={columnSizing}
          onCheckboxToggle={handleCheckboxToggle}
        />
      ));

  const renderTableContent = () => (
    <TableContainer>
      <Box
        component={"table"}
        sx={{
          borderCollapse: "separate",
          borderSpacing: "0px 1px",
          overflow: "auto",
          tableLayout: "fixed",
          minWidth: "100%",
          ...sx,
        }}
      >
        <TableHeader
          table={table}
          enableColumnResizing={enableColumnResizing}
          enableColumnReordering={enableColumnReordering}
        />
        <Box component={"tbody"}>
          {isLoading
            ? Array.from({ length: 20 }).map((_, index) => (
                <Box component={"tr"} key={`skeleton-${index}`}>
                  {table.getAllColumns().map((column, cIndex) => (
                    <Box
                      component={"td"}
                      key={column.id}
                      sx={{
                        p: "8px 16px",
                        ...getSkeletonCellSx(
                          column.id,
                          enableColumnResizing && column.getCanResize(),
                          cIndex === 0,
                        ),
                        overflowX: "clip",
                      }}
                    >
                      <Skeleton variant="text" height={"24px"} />
                    </Box>
                  ))}
                </Box>
              ))
            : table.getRowModel().rows.length > 0
              ? table.getRowModel().rows.map((row) => (
                  <Box
                    component={"tr"}
                    key={row.id}
                    onClick={() => handleRowClick(row.original)}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                      boxShadow: "0 1px 0 0px #EDEDED",
                      // Selected rows are purple 25 product-wide (UX audit, Oct 2026).
                      backgroundColor: row?.getIsSelected()
                        ? theme.palette.primitive.brand.purple[25]
                        : "transparent",
                      ":hover": {
                        borderRadius: "8px",
                        boxShadow:
                          "0 0 0 1px #EDEDED, -4px 9px 11px 0px #00000014",
                      },
                      // Opt-in row-hover reveal: any cell element tagged with
                      // `row-hover-action` (and hidden via its own opacity:0)
                      // becomes visible while the row is hovered or focused.
                      // No-op for rows whose cells don't use the class.
                      ":hover .row-hover-action, :focus-within .row-hover-action":
                        {
                          opacity: 1,
                        },
                    }}
                  >
                    {enableColumnReordering ? (
                      <SortableContext
                        items={columnIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {renderRowCells(row)}
                      </SortableContext>
                    ) : (
                      renderRowCells(row)
                    )}
                  </Box>
                ))
              : null}
        </Box>
      </Box>
    </TableContainer>
  );

  const renderTableContainer = () => {
    if (enableColumnReordering) {
      return (
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          {renderTableContent()}
        </DndContext>
      );
    }
    return renderTableContent();
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...containerSx,
      }}
    >
      {onPaginationChange && (
        <Box
          sx={{
            p: "4px 24px",
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
          }}
        >
          <PaginationV2
            currentPage={pageIndex}
            onPaginationClick={(page) => {
              onPaginationChange?.({
                pageIndex: page,
                pageSize: pageSize || 50,
              });
            }}
            // isDisabled={selectedItems?.length > 0}
            totalPages={table.getPageCount()}
            totalItems={rowCount}
            itemsPerPage={pageSize}
            onPerPageChange={(newSize) => {
              // Reset to page 1 on size change — the current page often doesn't
              // exist at the new size (page 8 @ 10/page has no page 8 @ 100),
              // which would render an empty table. Pagination is 1-based.
              onPaginationChange?.({ pageIndex: 1, pageSize: newSize });
            }}
            // pageNationStyles={pageNationStyles}
            isDataLoading={false}
            paginationLeftActionComponent={paginationLeftActionComponent}
          />
        </Box>
      )}

      {renderTableContainer()}

      {rowCount?.toString() === "0" && !isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            // minHeight: "300px", // Ensure minimum height for proper centering
            width: "100%",
          }}
        >
          <img
            src={NoDataImage}
            style={{ width: "120px" }}
            alt="No data found"
          />
          <Typography sx={{}} variant="body-large">
            No data found
          </Typography>
        </Box>
      ) : null}

      {/* Pagination */}
      {showActionToolbar && (customActionToolbar ? customActionToolbar : <></>)}
    </Box>
  );
}

export default Table;
